import { apiClient } from '../apiClient';
import type {
  ParsedCsvResult,
  BulkIdValidationResult,
  DetectedModulesResult,
  MassMigrationBatchSummaryDto,
  MassMigrationBatchDto,
  MassMigrationOrgType,
  ParsedCsvRow,
} from '@/types/massMigration';

export const massMigrationApi = {
  validateCsv: async (file: File, orgType: MassMigrationOrgType): Promise<ParsedCsvResult> => {
    const form = new FormData();
    form.append('file', file);
    form.append('orgType', orgType);
    // The apiClient instance defaults to Content-Type: application/json, which makes
    // axios serialize FormData to JSON instead of sending multipart/form-data — the
    // API then rejects with "The file field is required." Overriding to multipart
    // here lets axios drop the header so the browser sets it with the boundary.
    const { data } = await apiClient.post<ParsedCsvResult>('/mass-migration/validate-csv', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  validateIds: async (
    rows: Array<{ rowNumber: number; bridgeId: string; amplifyOrgUuid?: string | undefined }>,
    orgType: MassMigrationOrgType,
  ): Promise<BulkIdValidationResult> => {
    const { data } = await apiClient.post<BulkIdValidationResult>(
      '/mass-migration/validate-ids',
      { rows, orgType },
    );
    return data;
  },

  detectModules: async (
    rows: Array<{ rowNumber: number; bridgeId: string }>,
  ): Promise<DetectedModulesResult> => {
    const { data } = await apiClient.post<DetectedModulesResult>(
      '/mass-migration/detect-modules',
      { rows },
    );
    return data;
  },

  startBatch: async (payload: {
    name: string;
    orgType: MassMigrationOrgType;
    rows: ParsedCsvRow[];
    selectionsByRow: Record<number, string[]>;
    overridesByRow: Record<number, Record<string, Record<string, string | null>>>;
    userMigrationEnabledRows: number[];
  }): Promise<{ batchId: string }> => {
    const { data } = await apiClient.post<{ batchId: string }>('/mass-migration/batches', payload);
    return data;
  },

  getBatches: async (): Promise<MassMigrationBatchSummaryDto[]> => {
    const { data } = await apiClient.get<MassMigrationBatchSummaryDto[]>('/mass-migration/batches');
    return data;
  },

  getBatch: async (id: string): Promise<MassMigrationBatchDto> => {
    const { data } = await apiClient.get<MassMigrationBatchDto>(`/mass-migration/batches/${id}`);
    return data;
  },

  cancelBatch: async (id: string): Promise<void> => {
    await apiClient.post(`/mass-migration/batches/${id}/cancel`);
  },
};

// Query key factory for TanStack Query
export const massMigrationKeys = {
  all: ['mass-migration'] as const,
  batches: () => [...massMigrationKeys.all, 'batches'] as const,
  batch: (id: string) => [...massMigrationKeys.all, 'batch', id] as const,
};
